import { ProjectGeoDataByIri } from 'src/constants/projectGeoData';
import {
  MAP_LATITUDE_IRI,
  MAP_LOCATION_IRI,
  MAP_LONGITUDE_IRI,
  MAP_NODE_SIZE_IRI,
  MAP_POPUP_CONTENT_IRI,
  MAP_POPUP_TITLE_IRI,
  MAP_TOOLTIP_CONTENT_IRI,
  MAP_TOOLTIP_TITLE_IRI,
} from 'src/templates/queries/iris';

export const GEO_DATA_KEY_BY_IRI: Record<string, keyof ProjectGeoDataByIri> = {
  [MAP_LOCATION_IRI]: 'loc',
  [MAP_LATITUDE_IRI]: 'lat',
  [MAP_LONGITUDE_IRI]: 'long',
  [MAP_NODE_SIZE_IRI]: 'nodeSize',
  [MAP_POPUP_TITLE_IRI]: 'popupTitle',
  [MAP_POPUP_CONTENT_IRI]: 'popupContent',
  [MAP_TOOLTIP_TITLE_IRI]: 'tooltipTitle',
  [MAP_TOOLTIP_CONTENT_IRI]: 'tooltipContent',
};

/**
 * Determines the geospatial data for a set of IRIs with geospatial predicates.
 *
 * This query is used to provide geospatial metadata for classes across the application.
 *
 * **Call Frequency**
 *
 * For project models, geospatial data is captured once when the model is added to a project.
 *
 * **Context**
 * - Named graphs: set by the user (default is `reasoning.schema.graphs` value,
 *   which by default is `tag:stardog:api:context:local`)
 * - Reasoning: always off
 *
 * **Example**
 * ```sparql
# Geospatial Data
CONSTRUCT {
  ?subject <http://www.opengis.net/ont/geosparql#asWKT> ?loc .
  ?subject <http://www.w3.org/2003/01/geo/wgs84_pos#lat> ?lat .
  ?subject <http://www.w3.org/2003/01/geo/wgs84_pos#long> ?long .
  ?subject <tag:stardog:map:v1:node_size> ?nodeSize .
  ?subject <tag:stardog:map:v1:popup_title> ?popupTitle .
  ?subject <tag:stardog:map:v1:popup_content> ?popupContent .
  ?subject <tag:stardog:map:v1:tooltip_title> ?tooltipTitle .
  ?subject <tag:stardog:map:v1:tooltip_content> ?tooltipContent
}
WHERE {
  VALUES ?subject {
    <http://api.stardog.com/Alice> <http://api.stardog.com/ACME>
  }
  OPTIONAL {
    ?subject <http://www.opengis.net/ont/geosparql#asWKT> ?loc
  }
  OPTIONAL {
    ?subject <http://www.w3.org/2003/01/geo/wgs84_pos#lat> ?lat
  }
  OPTIONAL {
    ?subject <http://www.w3.org/2003/01/geo/wgs84_pos#long> ?long
  }
  OPTIONAL {
    ?subject <tag:stardog:map:v1:node_size> ?nodeSize
  }
  OPTIONAL {
    ?subject <tag:stardog:map:v1:popup_title> ?popupTitle
  }
  OPTIONAL {
    ?subject <tag:stardog:map:v1:popup_content> ?popupContent
  }
  OPTIONAL {
    ?subject <tag:stardog:map:v1:tooltip_title> ?tooltipTitle
  }
  OPTIONAL {
    ?subject <tag:stardog:map:v1:tooltip_content> ?tooltipContent
  }
}
 * ```
 */
export const buildGeoDataQuery = (iris: string[]) => {
  return `# Geospatial Data
CONSTRUCT {
  ${Object.entries(GEO_DATA_KEY_BY_IRI)
    .map((pair) => `?subject <${pair[0]}> ?${pair[1]} .`)
    .join('\n  ')}
}
WHERE {
  VALUES ?subject {  
    ${iris.map((iri) => `<${iri}>`).join(' ')}
  }
  OPTIONAL {
    ?subject <${MAP_LOCATION_IRI}> ?loc
  }
  OPTIONAL {
    ?subject <${MAP_LATITUDE_IRI}> ?lat
  }
  OPTIONAL {
    ?subject <${MAP_LONGITUDE_IRI}> ?long
  }
  OPTIONAL {
    ?subject <${MAP_NODE_SIZE_IRI}> ?nodeSize
  }
  OPTIONAL {
    ?subject <${MAP_POPUP_TITLE_IRI}> ?popupTitle
  }
  OPTIONAL {
    ?subject <${MAP_POPUP_CONTENT_IRI}> ?popupContent
  }
  OPTIONAL {
    ?subject <${MAP_TOOLTIP_TITLE_IRI}> ?tooltipTitle
  }
  OPTIONAL {
    ?subject <${MAP_TOOLTIP_CONTENT_IRI}> ?tooltipContent
  }
}`;
};
